/**
 * Per-route error boundary.
 *
 * Same machinery as RootErrorBoundary, but the default fallback is an
 * inline panel rather than a full-screen takeover. Mount one inside each
 * <Route element={...}> so a crash on one page doesn't blank out the
 * navigation chrome.
 */

import React from 'react';

import { RootErrorBoundary } from './RootErrorBoundary.jsx';

/**
 * @typedef {object} RouteErrorBoundaryProps
 * @property {React.ReactNode} children
 * @property {(error: Error, info: React.ErrorInfo) => void} [onError]
 * @property {React.ReactNode | ((error: Error, reset: () => void) => React.ReactNode)} [fallback]
 */

export function RouteErrorBoundary({ children, onError, fallback }) {
  // Defer to RootErrorBoundary, but pass our own inline fallback by default.
  return (
    <RootErrorBoundary
      onError={onError}
      fallback={fallback ?? ((error, reset) => <InlineFallback error={error} onReset={reset} />)}
    >
      {children}
    </RootErrorBoundary>
  );
}

function InlineFallback({ onReset }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-slate-300 bg-slate-50 p-6 text-center"
    >
      <p className="text-slate-700 mb-4">
        This section couldn&apos;t load. Try again, or navigate back.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="min-h-touch px-4 py-2 rounded-md border border-slate-400 text-slate-700 hover:bg-slate-100"
      >
        Try again
      </button>
    </div>
  );
}
